import React, { useState, useEffect, useCallback } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useSync } from '../context/SyncContext';
import { useAccounts } from '../context/AccountContext';
import { mailboxService } from '../services/mailboxService';

function Layout() {
  const { user, logout } = useAuth();
  const { syncProgress, startSync, backgroundSyncing } = useSync();
  const { mailboxes, activeMailboxes, isMultiAccount, activeMailboxIds, toggleMailbox, selectAll } = useAccounts();
  const navigate = useNavigate();
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [showAccountPicker, setShowAccountPicker] = useState(false);

  // Treat "real backend sync in progress" as a disable signal too, otherwise
  // a user can spam the button between SignalR ticks and queue 6 syncs in 2s
  // — exactly the loop reported in pic 2 / pic 5 on 2026-05-19.
  const realSyncInFlight = syncProgress && syncProgress.status === 'syncing';
  const syncDisabled = syncing || realSyncInFlight;

  // Add padding when sync bar is visible
  const hasSyncBar = syncProgress && syncProgress.status !== 'idle';

  const activeMailboxKey = [...activeMailboxIds].sort().join(',');

  useEffect(() => {
    loadFolders();
  }, [activeMailboxKey]);

  const loadFolders = async () => {
    try {
      // Load folders from the first active mailbox (folders are per-mailbox
      // but the standard types — Inbox, Sent, Drafts etc — are the same)
      const ids = [...activeMailboxIds];
      if (ids.length > 0) {
        const data = await mailboxService.getFolders(ids[0]);
        setFolders(data);
      } else {
        const data = await mailboxService.getFolders();
        setFolders(data);
      }
    } catch (error) {
      console.error('[Webmail] Failed to load folders:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCompose = () => {
    navigate('/compose');
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Sync ALL active mailboxes in parallel. Handles "already in flight" gracefully.
  const handleManualSync = useCallback(async () => {
    if (syncDisabled) return;
    setSyncing(true);
    try {
      const items = activeMailboxes.length > 0 ? activeMailboxes : mailboxes;
      if (items.length === 0) {
        alert('No mailbox configured yet. Add one in Settings \u2192 Email Accounts.');
        return;
      }

      const syncPromises = items.map(async (mb) => {
        const mailboxId = mb.mailboxId || mb.mailboxID || mb.id;
        const mailboxEmail = mb.emailAddress || mb.email;
        try {
          startSync && startSync(mailboxId, mailboxEmail);
        } catch { /* best-effort progress bar init */ }
        try {
          const result = await mailboxService.syncMailbox(mailboxId, { fullSync: false });
          // "already in flight" is fine — means the background service is handling it
          if (result?.alreadyRunning) {
            console.log(`[Webmail] Sync already running for ${mailboxEmail}`);
          }
          return result;
        } catch (err) {
          console.error(`[Webmail] Sync failed for ${mailboxEmail}:`, err);
        }
      });

      await Promise.all(syncPromises);
    } catch (err) {
      console.error('[Webmail] Manual sync failed:', err);
    } finally {
      setTimeout(() => setSyncing(false), 1500);
    }
  }, [syncDisabled, startSync, activeMailboxes, mailboxes]);

  const getFolderIcon = (folderType) => {
    const icons = {
      1: '📥', // Inbox
      2: '📤', // Sent
      3: '📝', // Drafts
      4: '🗑️', // Trash
      5: '⚠️', // Junk
      6: '📦', // Archive
      99: '📁' // Custom
    };
    return icons[folderType] || '📁';
  };

  return (
    <div className="app-layout" style={{ paddingTop: hasSyncBar ? '60px' : 0 }}>
      {/* Sidebar */}
      <aside className="sidebar">
        {/* Logo + manual Send/Receive */}
        <div className="logo" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div className="logo-icon">W</div>
          <span className="logo-text" style={{ flex: 1 }}>Webmail</span>
          {/* Subtle pulsing dot for background sync — no banner needed */}
          {backgroundSyncing && !syncing && (
            <span style={{
              width: 6, height: 6, borderRadius: '50%',
              background: 'var(--accent-color, #f97316)',
              animation: 'webmailBgSyncPulse 1.5s ease-in-out infinite',
              marginRight: 2,
            }} title="Syncing in background..." />
          )}
          <button
            type="button"
            onClick={handleManualSync}
            title={realSyncInFlight ? 'Sync already in progress' : 'Send / Receive all — sync your mailbox now'}
            disabled={syncDisabled}
            style={{
              background: 'none',
              border: '1px solid var(--border-color)',
              color: 'var(--text-muted)',
              width: 30,
              height: 30,
              borderRadius: 6,
              cursor: syncDisabled ? 'wait' : 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 0,
              fontSize: 14,
              lineHeight: 1,
              opacity: syncDisabled ? 0.6 : 1,
            }}
            aria-label="Send and receive all"
          >
            <span style={{
              display: 'inline-block',
              transformOrigin: '50% 50%',
              animation: (syncing || realSyncInFlight) ? 'webmailRefreshSpin 1s linear infinite' : 'none',
            }}>↻</span>
          </button>
        </div>
        {/* Inline keyframes — the rest of the layout uses inline styles so
            we keep this self-contained instead of adding to App.css. */}
        <style>{`
          @keyframes webmailRefreshSpin {
            from { transform: rotate(0deg); }
            to   { transform: rotate(360deg); }
          }
          @keyframes webmailBgSyncPulse {
            0%, 100% { opacity: 0.4; transform: scale(1); }
            50% { opacity: 1; transform: scale(1.3); }
          }
        `}</style>

        {/* Compose Button */}
        <button className="compose-btn" onClick={handleCompose}>
          <span>✏️</span>
          <span>Compose</span>
        </button>

        {/* Multi-account picker — only shown when 2+ accounts loaded */}
        {isMultiAccount && (
          <div className="account-picker" style={{ padding: '8px 12px' }}>
            <button
              type="button"
              onClick={() => setShowAccountPicker(!showAccountPicker)}
              style={{
                width: '100%',
                background: 'var(--card-bg)',
                border: '1px solid var(--border-color)',
                borderRadius: 8,
                padding: '8px 12px',
                color: 'var(--text-primary)',
                cursor: 'pointer',
                fontSize: 12,
                textAlign: 'left',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {activeMailboxes.length === mailboxes.length
                  ? `All accounts (${mailboxes.length})`
                  : `${activeMailboxes.length} of ${mailboxes.length} accounts`}
              </span>
              <span style={{ fontSize: 10, marginLeft: 4 }}>{showAccountPicker ? '▲' : '▼'}</span>
            </button>
            {showAccountPicker && (
              <div style={{
                marginTop: 4,
                background: 'var(--card-bg)',
                border: '1px solid var(--border-color)',
                borderRadius: 8,
                padding: '6px 0',
                boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
              }}>
                <div
                  style={{
                    padding: '4px 12px',
                    fontSize: 11,
                    color: 'var(--text-muted)',
                    borderBottom: '1px solid var(--border-color)',
                    marginBottom: 4,
                    cursor: 'pointer',
                  }}
                  onClick={selectAll}
                >
                  Select All
                </div>
                {mailboxes.map((mb) => {
                  const mbId = mb.mailboxId || mb.mailboxID || mb.id;
                  const email = mb.emailAddress || mb.email;
                  const isChecked = activeMailboxIds.has(mbId);
                  return (
                    <label
                      key={mbId}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        padding: '6px 12px',
                        cursor: 'pointer',
                        fontSize: 12,
                        color: 'var(--text-primary)',
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={() => toggleMailbox(mbId)}
                        style={{ accentColor: 'var(--accent-color, #f97316)' }}
                      />
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {email}
                      </span>
                    </label>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Scrollable nav region (folders + tools) so the lists can grow
            past the viewport without pushing Contacts/Settings/AgentCore
            off-screen. Compose stays pinned above, the user card below. */}
        <div className="sidebar-nav">
        {/* Folders Navigation */}
        <nav className="nav-section">
          <div className="nav-title">Folders</div>
          
          {loading ? (
            <div style={{ padding: '12px', color: 'var(--text-muted)' }}>Loading...</div>
          ) : (
            folders.map(folder => (
              <NavLink
                key={folder.folderId}
                to={`/inbox/${folder.folderId}`}
                end
                className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
              >
                <span className="nav-item-icon">{getFolderIcon(folder.folderType)}</span>
                <span>{folder.name}</span>
                {folder.unreadCount > 0 && (
                  <span className="nav-item-badge">{folder.unreadCount}</span>
                )}
              </NavLink>
            ))
          )}
          
          {/* Fallback if no folders loaded */}
          {!loading && folders.length === 0 && (
            <>
              <NavLink to="/inbox" className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
                <span className="nav-item-icon">📥</span>
                <span>Inbox</span>
              </NavLink>
              <NavLink to="/inbox/sent" className="nav-item">
                <span className="nav-item-icon">📤</span>
                <span>Sent</span>
              </NavLink>
              <NavLink to="/inbox/drafts" className="nav-item">
                <span className="nav-item-icon">📝</span>
                <span>Drafts</span>
              </NavLink>
              <NavLink to="/inbox/trash" className="nav-item">
                <span className="nav-item-icon">🗑️</span>
                <span>Trash</span>
              </NavLink>
            </>
          )}
        </nav>

        {/* Other Navigation */}
        <nav className="nav-section">
          <div className="nav-title">Tools</div>
          <NavLink to="/calendar" className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
            <span className="nav-item-icon">📅</span>
            <span>Calendar</span>
          </NavLink>
          <NavLink to="/contacts" className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
            <span className="nav-item-icon">👥</span>
            <span>Contacts</span>
          </NavLink>
          <NavLink to="/settings" className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
            <span className="nav-item-icon">⚙️</span>
            <span>Settings</span>
          </NavLink>
          <NavLink to="/agentcore" className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
            <span className="nav-item-icon">🤖</span>
            <span>AgentCore</span>
          </NavLink>
        </nav>
        </div>

        {/* User Section */}
        <div style={{ marginTop: 'auto', padding: '16px 12px', borderTop: '1px solid var(--border-color)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div className="sender-avatar" style={{ width: '36px', height: '36px', fontSize: '14px' }}>
              {user?.displayName?.[0] || user?.email?.[0] || 'U'}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: '14px', fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {user?.displayName || 'User'}
              </div>
              <div style={{ fontSize: '12px', color: 'var(--text-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {user?.email}
              </div>
            </div>
            <button 
              onClick={handleLogout}
              style={{ 
                background: 'none', 
                border: 'none', 
                cursor: 'pointer', 
                color: 'var(--text-muted)',
                fontSize: '16px'
              }}
              title="Logout"
            >
              🚪
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="main-content">
        {/* Header */}
        <header className="header">
          <div className="search-bar">
            <span className="search-icon">🔍</span>
            <input 
              type="text" 
              className="search-input" 
              placeholder={isMultiAccount ? "Search selected accounts..." : "Search emails, contacts, or events..."}
            />
            {isMultiAccount && (
              <span style={{
                fontSize: 10,
                color: 'var(--text-muted)',
                background: 'var(--card-bg)',
                padding: '2px 6px',
                borderRadius: 4,
                marginRight: 8,
                whiteSpace: 'nowrap',
              }}>
                {activeMailboxes.length === mailboxes.length
                  ? 'All accounts'
                  : `${activeMailboxes.length} account${activeMailboxes.length > 1 ? 's' : ''}`}
              </span>
            )}
          </div>
          <button className="action-btn" title="AI Assistant">
            ✨
          </button>
          <button className="action-btn" title="Notifications">
            🔔
          </button>
        </header>

        {/* Page Content */}
        <Outlet />
      </main>
    </div>
  );
}

export default Layout;

